import { Injectable } from '@angular/core';
import { SyncServerServiceProvider } from '../sync-server-service/sync-server-service';

/**
 * Resolves the district a project belongs to, and narrows a lab list to it.
 *
 * LADOTD carries the district on the project rather than in the lab model - there is no District
 * entity, and no column on Lab. It lives in `Project.CustomProperties`, written by the customer's
 * own project sync, as `labNumber` (with `districtNumber` alongside it, always the same value).
 * `gangNumber` names the field crew, and `<labNumber>-<gangNumber>` is the external code of that
 * crew's lab.
 *
 * Labs express the same district in their name - `District 02 Lab`, `District 02 Gang 202`,
 * `District 02 Unit 202`, `District 02 Independent Assurance` - and in the leading digits of their
 * external code. Labs that belong to no district at all (the central Materials Lab, the consultant
 * labs) parse to nothing and are always kept, so the central lab survives without being named here.
 *
 * Everything about this is fail-open. A project with no district metadata, a lab list that would
 * narrow to nothing, an unreadable project record - each returns the list untouched. Roughly one
 * project in ten carries no district, and the newer AWP-sourced projects carry none at all, so the
 * unnarrowed case is normal rather than exceptional.
 */
@Injectable()
export class DistrictScopeProvider
{
    districtCode: string = '';
    crewCode: string = '';
    fieldLabCode: string = '';
    private resolvedIDProject: number = 0;
    private resolveInFlight: Promise<void> = null;

    constructor(private syncService: SyncServerServiceProvider)
    {
    }

    /**
     * Read the district off a project, once per project.
     *
     * The app's usual project fetch is a Lite projection that omits CustomProperties, so this reads
     * the full record.
     *
     * @param {number} pIDProject
     * @return {Promise<void>}
     */
    async resolveForProject(pIDProject): Promise<void>
    {
        const tmpIDProject = Number(pIDProject) || 0;
        if (!tmpIDProject || tmpIDProject === this.resolvedIDProject)
        {
            return;
        }
        if (this.resolveInFlight)
        {
            return this.resolveInFlight;
        }
        this.resolveInFlight = (async () =>
        {
            try
            {
                this.districtCode = '';
                this.crewCode = '';
                this.fieldLabCode = '';
                const tmpProject: any = await this.syncService.gopher(`/${ tmpIDProject }`, 'Project', 'Project:DistrictScope');
                const tmpProperties = this.readCustomProperties(tmpProject);
                const tmpDistrict = tmpProperties['labNumber'] || tmpProperties['districtNumber'] || '';
                // unitNumber first, mirroring the customer's Gang -> Unit rename on the project side
                // as it lands. gangNumber stays the fallback for as long as projects carry it, and
                // the two name the same crew, so neither generation of metadata is left behind.
                const tmpCrew = tmpProperties['unitNumber'] || tmpProperties['gangNumber'] || '';
                this.districtCode = this.normaliseCode(tmpDistrict);
                this.crewCode = this.normaliseCode(tmpCrew);
                if (this.districtCode && this.crewCode)
                {
                    this.fieldLabCode = `${ this.districtCode }-${ this.crewCode }`;
                }
                this.resolvedIDProject = tmpIDProject;
            }
            finally
            {
                this.resolveInFlight = null;
            }
        })();
        return this.resolveInFlight;
    }

    /**
     * CustomProperties as an object, whether it arrives parsed or as a JSON string.
     *
     * @param {any} pProject
     * @return {any}
     */
    readCustomProperties(pProject): any
    {
        const tmpRaw = pProject ? pProject.CustomProperties : null;
        if (!tmpRaw)
        {
            return {};
        }
        if (typeof tmpRaw === 'string')
        {
            try
            {
                return JSON.parse(tmpRaw) || {};
            }
            catch (pError)
            {
                return {};
            }
        }
        return tmpRaw;
    }

    /**
     * A district or crew number as the digits labs use in their names and codes.
     *
     * @param {any} pValue
     * @return {string} Digits only, or '' when there is nothing usable.
     */
    normaliseCode(pValue): string
    {
        const tmpValue = String(pValue == null ? '' : pValue).trim();
        if (!/^\d+$/.test(tmpValue))
        {
            return '';
        }
        // `0` is the customer's "unassigned" sentinel, and `001` appears as a crew placeholder on
        // several hundred projects. Neither names a real lab.
        const tmpNumeric = Number(tmpValue);
        if (!tmpNumeric)
        {
            return '';
        }
        return tmpValue;
    }

    /**
     * Whether a lab is a top level lab rather than a discipline beneath one.
     *
     * The picker rewrites a parent lab's IDLabParent to its own IDLab when it merges parents and
     * children into one list, so a root arrives carrying a non-zero parent that points at itself.
     * Reading IDLabParent alone therefore misreads every parent as a child.
     *
     * @param {any} pLab
     * @return {boolean}
     */
    isRootLab(pLab): boolean
    {
        const tmpParent = Number(pLab.IDLabParent) || 0;
        return !tmpParent || tmpParent === Number(pLab.IDLab);
    }

    /**
     * The district a lab belongs to.
     *
     * Name first because it is the consistent form across every district-bound lab; the external
     * code is the fallback for the few whose name does not carry it.
     *
     * @param {any} pLab
     * @return {string} District digits, or '' for a lab that belongs to no district.
     */
    parseLabDistrict(pLab): string
    {
        if (!pLab)
        {
            return '';
        }
        const tmpName = String(pLab.Name || '');
        const tmpByName = tmpName.match(/^District\s+(\d+)/i);
        if (tmpByName)
        {
            return tmpByName[1];
        }
        const tmpCode = String(pLab.ExternalLabID || '');
        const tmpByCode = tmpCode.match(/^(\d{2})/);
        return tmpByCode ? tmpByCode[1] : '';
    }

    /**
     * Whether a lab is the district's own laboratory, as opposed to a field crew, an independent
     * assurance lab or a coring lab in the same district.
     *
     * Recognised by name. The customer names exactly one lab per district `District NN Lab`, and it
     * is the only one of that district's labs whose name ends there.
     *
     * @param {any} pLab
     * @return {boolean}
     */
    isDistrictLab(pLab): boolean
    {
        if (!pLab || !this.districtCode || !this.isRootLab(pLab))
        {
            return false;
        }
        const tmpMatch = String(pLab.Name || '').match(/^District\s+(\d+)\s+Lab$/i);
        return Boolean(tmpMatch) && Number(tmpMatch[1]) === Number(this.districtCode);
    }

    /**
     * Whether a lab is the project's own field crew lab.
     *
     * Matched on the external code, which both generations share - the Gang lab and its Unit
     * replacement carry the same `<district>-<crew>` value. Only one of the pair survives the
     * specification-set scoping applied before this, so whichever remains is the right generation
     * for the project and no Gang/Unit decision is needed here.
     *
     * Compared numerically on both halves so project metadata does not have to be zero-padded.
     *
     * @param {any} pLab
     * @return {boolean}
     */
    isFieldLab(pLab): boolean
    {
        if (!pLab || !this.districtCode || !this.crewCode)
        {
            return false;
        }
        const tmpMatch = String(pLab.ExternalLabID || '').match(/^(\d+)-(\d+)$/);
        if (!tmpMatch)
        {
            return false;
        }
        return Number(tmpMatch[1]) === Number(this.districtCode)
            && Number(tmpMatch[2]) === Number(this.crewCode);
    }

    /**
     * The labs a project should arrive with selected: its district laboratory and its own field
     * crew's lab.
     *
     * @param {Array<any>} pLabs The decorated lab rows, after any narrowing.
     * @return {Array<number>} IDLab values to select. Empty when the project carries no district.
     */
    getDefaultLabIDs(pLabs): Array<number>
    {
        if (!this.districtCode || !pLabs || !pLabs.length)
        {
            return [];
        }
        const tmpDefaults = [];
        pLabs.forEach((pLab: any) =>
        {
            if (this.isDistrictLab(pLab) || this.isFieldLab(pLab))
            {
                tmpDefaults.push(Number(pLab.IDLab));
            }
        });
        return tmpDefaults;
    }

    /**
     * Narrow a decorated lab list to the project's district.
     *
     * Applied at the root level, with a child kept whenever its parent survives - the district is a
     * property of the family, and the disciplines beneath a district lab do not each restate it.
     *
     * @param {Array<any>} pLabs
     * @return {Array<any>} The narrowed list, or the input when there is no district to narrow by
     * or narrowing would empty it.
     */
    narrowLabsToDistrict(pLabs): Array<any>
    {
        if (!this.districtCode || !pLabs || !pLabs.length)
        {
            return pLabs;
        }
        const tmpKeptRoots = new Set();
        let tmpMatchedDistrict = 0;
        pLabs.forEach((pLab: any) =>
        {
            if (!this.isRootLab(pLab))
            {
                return;
            }
            const tmpDistrict = this.parseLabDistrict(pLab);
            if (!tmpDistrict)
            {
                tmpKeptRoots.add(Number(pLab.IDLab));
                return;
            }
            // Compared as numbers so a project carrying `2` and a lab named `District 02` still
            // match. Requiring the caller to zero-pad would make a silent, total loss of the user's
            // own district depend on a formatting detail nobody would think to check.
            if (Number(tmpDistrict) === Number(this.districtCode))
            {
                ++tmpMatchedDistrict;
                tmpKeptRoots.add(Number(pLab.IDLab));
            }
        });
        // No lab carries this district - an administrative code like 98 or 99, or a district with
        // no labs yet. The district-less labs alone would survive, leaving the user with the
        // central lab and nothing of their own, so treat it as unscoped instead.
        if (!tmpMatchedDistrict)
        {
            return pLabs;
        }
        const tmpNarrowed = pLabs.filter((pLab: any) =>
        {
            return this.isRootLab(pLab)
                ? tmpKeptRoots.has(Number(pLab.IDLab)) : tmpKeptRoots.has(Number(pLab.IDLabParent));
        });
        return tmpNarrowed.length ? tmpNarrowed : pLabs;
    }
}



// WEBPACK FOOTER //
// ./src/providers/district-scope-service/district-scope.service.ts